import { paths } from "components/App/paths";

export interface NavItem {
  permission: boolean;
  path: string;
}

export type MonitorPermissions = Record<
  "events" | "connectorHealth" | "authFlowMonitoring" | "managedBankFeedsSyncs",
  NavItem
>;

export type SettingsPermissions = Record<
  | "linkSettings"
  | "branding"
  | "redirects"
  | "integrations"
  | "otherIntegrations"
  | "bankFeeds"
  | "dataTypes"
  | "users"
  | "products"
  | "billing"
  | "rules"
  | "webhookSecurity",
  NavItem
>;
export type DevelopersPermissions = Record<"deprecations" | "apiKeys", NavItem>;

export interface NavPermissionMap {
  home: NavItem;
  companies: NavItem;
  products: {
    syncCommerce: NavItem;
    syncExpenses: NavItem;
    accountingApi: NavItem;
    bankingApi: NavItem;
    commerceApi: NavItem;
  };
  monitor: MonitorPermissions;
  settings: SettingsPermissions;
  developers: DevelopersPermissions;
}

export function createNavPermissionMap(
  userPermissions: string[],
  userRole?: string
): NavPermissionMap {
  return {
    home: {
      permission: true,
      path: paths.HOME,
    },
    companies: {
      permission: userPermissions.includes("clients_companies_view"),
      path: paths.COMPANIES,
    },
    products: {
      syncCommerce: {
        permission: true,
        path: paths.products.SYNC_FOR_COMMERCE,
      },
      syncExpenses: {
        permission: true,
        path: paths.products.SYNC_FOR_EXPENSES,
      },
      accountingApi: {
        permission: true,
        path: `${paths.products.ACCOUNTING_API}${paths.products.NESTED_ROUTES.PRODUCT_OVERVIEW}`,
      },
      bankingApi: {
        permission: true,
        path: `${paths.products.BANKING_API}${paths.products.NESTED_ROUTES.PRODUCT_OVERVIEW}`,
      },
      commerceApi: {
        permission: true,
        path: `${paths.products.COMMERCE_API}${paths.products.NESTED_ROUTES.PRODUCT_OVERVIEW}`,
      },
    },
    monitor: {
      events: {
        permission: userPermissions.includes("clients_alerts_view"),
        path: paths.monitor.EVENTS,
      },
      connectorHealth: {
        permission: true,
        path: paths.monitor.CONNECTOR_HEALTH,
      },
      authFlowMonitoring: {
        permission: userPermissions.includes("clients_link_conversion_view"),
        path: paths.monitor.AUTH_FLOW_OVERVIEW,
      },
      managedBankFeedsSyncs: {
        permission: true,
        path: paths.monitor.MANAGED_BANK_FEEDS_SYNCS,
      },
    },
    settings: {
      linkSettings: {
        permission: userPermissions.includes("authFlow_view"),
        path: paths.settings.LINK_SETTINGS,
      },
      branding: {
        permission: userPermissions.includes("clients_companyProfile_edit"),
        path: paths.settings.BRANDING,
      },
      redirects: {
        permission: userPermissions.includes("clients_companyProfile_edit"),
        path: paths.settings.REDIRECTS,
      },
      integrations: {
        permission: userPermissions.includes("clients_integrations_view"),
        path: paths.settings.INTEGRATIONS_ACCOUNTING,
      },
      otherIntegrations: {
        permission: true,
        path: paths.settings.INTEGRATIONS_OTHER,
      },
      bankFeeds: {
        permission: true,
        path: paths.settings.INTEGRATIONS_BANK_FEEDS,
      },
      dataTypes: {
        permission: userPermissions.includes("clients_syncSettings_view"),
        path: paths.settings.DATA_TYPES,
      },
      users: {
        permission: userPermissions.includes("users_view"),
        path: paths.settings.USERS,
      },
      products: {
        permission: true,
        path: paths.settings.MANAGE_PRODUCTS,
      },
      billing: {
        permission: userPermissions.includes("clients_settings_edit"),
        path: paths.settings.BILLING,
      },
      rules: {
        permission:
          userPermissions.includes("clients_rules_view") &&
          userPermissions.includes("clients_alerts_view"),
        path: paths.settings.RULES,
      },
      webhookSecurity: {
        permission: userPermissions.includes("clients_companyProfile_edit"),
        path: paths.settings.WEBHOOK_SECURITY,
      },
    },
    developers: {
      apiKeys: {
        permission:
          userRole === "Administrator" || userRole === "Developer" || false,
        path: paths.developers.API_KEYS,
      },
      deprecations: {
        permission:
          userRole === "Administrator" || userRole === "Developer" || false,
        path: paths.developers.DEPRECATIONS,
      },
    },
  };
}

export const analyticsPageNames: { path: RegExp; name: string }[] = [
  {
    path: /\/settings$/,
    name: "Settings Overview",
  },
  {
    path: /\/link-settings/,
    name: "Link Settings",
  },
  {
    path: /\/alerts/,
    name: "Raised Alerts",
  },
  {
    path: /\/rules/,
    name: "Alerting Rules",
  },
  {
    path: /\/organization/,
    name: "Manage Organization",
  },
  {
    path: /\/data-types/,
    name: "Data Type Settings",
  },
  {
    path: /\/dataSets\/[\w-]+/,
    name: "Data History",
  },
  {
    path: /\/companies\/[\w-]+\/settings/,
    name: "Company Settings",
  },
  {
    path: /(\/companies\/?)$/,
    name: "Companies",
  },
  {
    path: /^\/$/,
    name: "Home",
  },
];

export function canViewNavGroupLink(navItems?: Record<string, NavItem>) {
  return (
    !!navItems &&
    Object.values(navItems).some((item) => item?.permission === true)
  );
}

export const getNavGroupFirstPagePathOrDefault = (
  navItems?: Record<string, NavItem>
) => {
  const page =
    navItems && Object.keys(navItems).find((item) => navItems[item].permission);
  return (page && navItems[page]?.path) ?? "";
};
